import React, { useEffect, useMemo, useState } from 'react'
import { useIdentity } from '@equipmentshare/auth0-react'
import ComboBox from 'components/ComboBox/comboBox'
import FormRow, { FormRowProps } from 'components/FormRow'
import useInvoicesQuery, { Invoice } from '../../hooks/useInvoicesQuery'

/** Invoice search is only wired up for EquipmentShare's own company. */
const INVOICE_SEARCH_COMPANY_ID = 1854

export type InvoicePickerProps = {
	/** Selected invoice ID */
	selectedInvoiceId?: string,
	/** Callback when invoice selection changes */
	onChange: ( invoiceId: string, invoice?: Invoice ) => void,
	/** Form row props for validation and styling */
	formRowProps?: Partial<FormRowProps>,
	/** Whether the picker is disabled */
	isDisabled?: boolean,
	/** Placeholder text */
	placeholder?: string,
	/** Prepend element for the combo box */
	prepend?: string | React.ReactElement | ( string | React.ReactElement )[],
	/** Test ID for testing */
	'data-testid'?: string,
	/** Branch ID to filter invoices by */
	branchId?: string,
}

export default function InvoicePicker( {
	selectedInvoiceId,
	onChange,
	formRowProps,
	isDisabled,
	placeholder = 'Search for invoices...',
	prepend,
	'data-testid': testId,
	branchId,
}: InvoicePickerProps ) {
	const identity = useIdentity()
	const { searchInvoices, invoices, loading } = useInvoicesQuery()

	const isInvoiceSearchAvailable = identity?.companyId === INVOICE_SEARCH_COMPANY_ID
	const [ searchString, setSearchString ] = useState( '' )

	// Held as state rather than searched straight from the callback so a branch
	// change re-scopes the current search — `ComboBox` only calls back on input.
	// The guards are the pre-conversion effect's: clearing must not search, and
	// neither must an ungated company.
	useEffect( () => {
		if ( searchString && isInvoiceSearchAvailable ) {
			searchInvoices( searchString, { branchId } )
		}
	}, [ searchString, searchInvoices, isInvoiceSearchAvailable, branchId ] )

	// Fetch pre-selected invoice when ID is provided without a prior search
	useEffect( () => {
		if ( selectedInvoiceId && isInvoiceSearchAvailable ) {
			searchInvoices( selectedInvoiceId, { branchId } )
		}
	}, [ selectedInvoiceId, searchInvoices, isInvoiceSearchAvailable, branchId ] )

	const selectedInvoice = useMemo( () => (
		selectedInvoiceId
			? invoices.find( ( invoice ) => invoice.invoiceId.toString() === selectedInvoiceId )
			: undefined
	), [ selectedInvoiceId, invoices ] )

	const handleChange = ( invoice?: Invoice ) => {
		onChange( invoice ? invoice.invoiceId.toString() : '', invoice )
	}

	return (
		<FormRow label="Invoice ID" {...formRowProps}>
			<ComboBox<Invoice>
				placeholder={isInvoiceSearchAvailable ? placeholder : 'Invoice search not available'}
				data={isInvoiceSearchAvailable ? invoices : []}
				getName={( invoice ) => `${invoice.invoiceNo} - ${invoice.company.name} (${invoice.invoiceId})`}
				selectedOption={selectedInvoice}
				setSelectedOption={handleChange}
				setSearchString={setSearchString}
				debounceDelayMs={400}
				// The pre-conversion `EuiComboBox` wasn't async, so it filtered the current
				// results by the typed text while the next search was pending.
				async={false}
				isLoading={isInvoiceSearchAvailable && loading}
				isDisabled={!isInvoiceSearchAvailable || Boolean( isDisabled )}
				isInvalid={formRowProps?.isInvalid}
				prepend={prepend}
				data-testid={testId}
			/>
		</FormRow>
	)
}
